import { useEffect, useState, type ReactNode } from 'react';
import { PhoneOff, Voicemail, Phone, PhoneForwarded, Bot } from 'lucide-react';
import { CallbackPicker } from '../../../components/CallbackPicker';
import { presetToIso } from '../../../lib/callbackPresets';
import type { CallbackPreset } from '../../../services/leadGenApi';
import type { CallOutcome } from '../../../types';

// Das neue Abschluss-Fenster nach dem Gespräch (ADR 0357 Punkt 13.6, ADR 0362, Stufe 2).
// Drei Schritte statt eines langen Panels: „Abgenommen?" → (nur bei Ja) „Ergebnis" → der eine Folgeschritt dieses
// Ergebnisses. Jeder Schritt hat „Zurück", der Kommentar ist überall erreichbar. Reine Anzeige: gespeichert wird im
// Cockpit (`save()` ist der einzige Schreibweg, L-6), hier kommt nur `onSpeichern(ergebnis, optionen)` heraus.

type Schritt = 'abgenommen' | 'ergebnis' | 'folge';
type Wahl = Extract<CallOutcome,
  'TERMIN' | 'FOLLOW UP' | 'MAIL GEWÜNSCHT' | 'LOST' | 'FALSCHER ANSPRECHPARTNER' | 'SEKRETARIAT'>;

/** Was das Fenster zusätzlich zum Ergebnis mitgibt. `callbackAt`: undefined = die Zeitwahl des Cockpits gilt,
 *  null = keine eigene Wiedervorlage (der Server rechnet „ab morgen früh"), sonst dieser Zeitpunkt. */
export interface AbschlussOptionen {
  callbackAt?: string | null;
  /** ADR 0372: der Setter hat die Zeit selbst gewählt („Besetzt“): sie gilt auch bei „nicht erreicht“. */
  wiedervorlageGewaehlt?: boolean;
  verweis?: { name: string; position: string | null; kanal: 'phone' };
}

const SECHS: { wahl: Wahl; label: string; taste: string; hinweis: string }[] = [
  { wahl: 'TERMIN',                   label: 'Termin',                    taste: 'T', hinweis: 'Zeit wählen, die Firma bleibt bei dieser Person' },
  { wahl: 'FOLLOW UP',                label: 'Follow up',                 taste: 'F', hinweis: 'Wiedervorlage wählen' },
  { wahl: 'MAIL GEWÜNSCHT',           label: 'Mail gewünscht',            taste: 'G', hinweis: 'danach öffnet sich das Mail-Fenster' },
  { wahl: 'LOST',                     label: 'Kein Interesse',            taste: 'K', hinweis: 'Wiedervorlage, Vorschlag aus den Einstellungen' },
  { wahl: 'FALSCHER ANSPRECHPARTNER', label: 'Jemand anderes zuständig',  taste: 'A', hinweis: 'wer ist es? Die Person fällt raus' },
  { wahl: 'SEKRETARIAT',              label: 'Sekretariat',               taste: 'S', hinweis: 'Firma ab morgen früh wieder dran' },
];

export function tageLabel(n: number): string {
  if (n === 30) return '1 Monat';
  if (n === 90) return '3 Monate';
  if (n % 7 === 0 && n < 60) return `${n / 7} ${n === 7 ? 'Woche' : 'Wochen'}`;
  return `${n} Tage`;
}

/** Zeitpunkt in `tage` Tagen um 09:00, Samstag und Sonntag springen auf Montag (gleiche Rechnung wie „morgen früh"). */
export function inTagen(tage: number): string {
  const p: CallbackPreset = { label: '', months: 0, days: tage, hours: 0, at: '09:00', default: false };
  return presetToIso(p);
}

/** Zeitpunkt in `stunden` Stunden (UTC-ISO mit Z), für „Besetzt · in 2 Std. nochmal“. */
export function inStunden(stunden: number, jetzt: number = Date.now()): string {
  return new Date(jetzt + stunden * 3_600_000).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export interface AnrufAbschlussProps {
  elapsed: number;
  saving: boolean;
  speicherFehler: boolean;
  notes: string;
  onNotes: (text: string) => void;
  picker: {
    presets: CallbackPreset[]; presetIdx: number | null; callbackAt: string | null; exactOpen: boolean;
    onPreset: (i: number) => void; onExact: (iso: string | null) => void;
    onToggleExact: () => void; onSavePreset: () => void;
    /** true, sobald ein Zeitpunkt gewählt ist (Termin braucht ihn). */
    hatZeit: boolean;
  };
  keinInteresseTage: number;
  mindestKlingelzeit: number;
  /** ADR 0372: nach wie vielen Stunden „Besetzt“ wieder anruft (Einstellung `besetzt_wiederanruf_stunden`). */
  besetztStunden: number;
  /** ADR 0385: die wievielte Anwahl dieser Person gerade läuft (1, 2, ...) und wie viele es je Tag sein sollen
   *  (Einstellung `anruf_versuche_pro_tag`). Solange `anwahl < anwahlenProTag`, speichern „Klingelt durch“ und
   *  „Mailbox“ noch nicht, sondern bitten um die nächste Anwahl. */
  anwahl: number;
  anwahlenProTag: number;
  /** „Nochmal wählen“: zurück in den Call, die nächste Anwahl wird gezählt. */
  onNochmal: () => void;
  /** Der Knopf „Nummer falsch / nicht vergeben" des Cockpits (bleibt dort, hier nur eingesetzt). */
  nummerNichtVergeben: ReactNode;
  onSpeichern: (ergebnis: CallOutcome, optionen: AbschlussOptionen) => void;
  onVerwerfen: () => void;
  onZurueckZumCall: () => void;
}

export default function AnrufAbschluss(p: AnrufAbschlussProps) {
  const [schritt, setSchritt] = useState<Schritt>('abgenommen');
  const [wahl, setWahl] = useState<Wahl | null>(null);
  const [kommentarOffen, setKommentarOffen] = useState(false);
  const [keinTage, setKeinTage] = useState(p.keinInteresseTage);
  const [wer, setWer] = useState('');
  const [position, setPosition] = useState('');
  const [weissNicht, setWeissNicht] = useState(false);
  // ADR 0385: „Klingelt durch“ oder „Mailbox“ vor der letzten Anwahl. Gespeichert wird erst nach der letzten Anwahl.
  const [keinerDran, setKeinerDran] = useState<'NICHT ERREICHT' | 'MAILBOX' | null>(null);
  const nochEineAnwahl = p.anwahl < p.anwahlenProTag;

  const sehrKurz = p.mindestKlingelzeit > 0 && p.elapsed < p.mindestKlingelzeit;
  const terminOhneZeit = wahl === 'TERMIN' && !p.picker.hatZeit;
  const verweisFehlt = wahl === 'FALSCHER ANSPRECHPARTNER' && !weissNicht && !wer.trim();
  const darfSpeichern = !p.saving && !!wahl && !terminOhneZeit && !verweisFehlt;

  const keinerRan = (o: 'NICHT ERREICHT' | 'MAILBOX') => {
    if (nochEineAnwahl) setKeinerDran(o);
    else p.onSpeichern(o, {});
  };

  const zurueck = () => {
    if (keinerDran) setKeinerDran(null);
    else if (schritt === 'folge') { setSchritt('ergebnis'); setWahl(null); }
    else if (schritt === 'ergebnis') setSchritt('abgenommen');
    else p.onZurueckZumCall();
  };

  const besetzt = () => {
    if (p.saving) return;
    p.onSpeichern('NICHT ERREICHT', { callbackAt: inStunden(p.besetztStunden), wiedervorlageGewaehlt: true });
  };

  // ADR 0389: ein Bot nimmt immer ab, eine zweite Anwahl bringt nichts. Ein Klick speichert, die nächste Person ist dran.
  const bot = () => {
    if (!p.saving) p.onSpeichern('BOT GATEKEEPER', {});
  };

  const speichern = () => {
    if (!darfSpeichern || !wahl) return;
    const opt: AbschlussOptionen = {};
    if (wahl === 'LOST') opt.callbackAt = inTagen(keinTage);
    else if (wahl === 'TERMIN' || wahl === 'FOLLOW UP') opt.callbackAt = undefined;
    else opt.callbackAt = null;
    if (wahl === 'FALSCHER ANSPRECHPARTNER' && !weissNicht && wer.trim()) {
      opt.verweis = { name: wer.trim(), position: position.trim() || null, kanal: 'phone' };
    }
    p.onSpeichern(wahl, opt);
  };

  // Tasten: nur wenn nicht gerade getippt wird (Kommentar, Name). Esc geht immer einen Schritt zurück.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const tippt = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (e.key === 'Escape') { e.preventDefault(); if (tippt) el?.blur(); else zurueck(); return; }
      if (tippt || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (keinerDran) {
        if (k === 'w' || e.key === 'Enter') { e.preventDefault(); p.onNochmal(); }
        else if (k === 's') { e.preventDefault(); p.onSpeichern(keinerDran, {}); }
      } else if (schritt === 'abgenommen') {
        if (k === 'n') { e.preventDefault(); keinerRan('NICHT ERREICHT'); }
        else if (k === 'b') { e.preventDefault(); besetzt(); }
        else if (k === 'm') { e.preventDefault(); keinerRan('MAILBOX'); }
        else if (k === 'k') { e.preventDefault(); bot(); }
        else if (k === 'j') { e.preventDefault(); setSchritt('ergebnis'); }
        else if (e.key === 'Enter') { e.preventDefault(); p.onVerwerfen(); }
      } else if (schritt === 'ergebnis') {
        const treffer = SECHS.find((s) => s.taste.toLowerCase() === k);
        if (treffer) { e.preventDefault(); setWahl(treffer.wahl); setSchritt('folge'); }
        else if (e.key === 'Backspace') { e.preventDefault(); zurueck(); }
      } else if (e.key === 'Enter') { e.preventDefault(); speichern(); }
      else if (e.key === 'Backspace') { e.preventDefault(); zurueck(); }
    };
    // Capture wie der Tastenhandler des Cockpits: dessen stopPropagation auf window trifft nur andere Knoten.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const knopf = 'w-full py-3 max-md:py-4 max-md:text-base rounded-xl border border-stark bg-bg-elevated text-text-main text-sm font-semibold '
    + 'hover:bg-bg-surface disabled:opacity-50 flex items-center justify-center gap-2';

  return (
    <div data-testid="anruf-abschluss" data-schritt={keinerDran ? 'nochmal' : schritt}>
      <h2 className="text-lg font-bold mb-1">
        {keinerDran && `Noch einmal anwählen (${p.anwahl + 1}. von ${p.anwahlenProTag})`}
        {!keinerDran && schritt === 'abgenommen' && 'Hat jemand abgenommen?'}
        {schritt === 'ergebnis' && 'Wie ging das Gespräch aus?'}
        {schritt === 'folge' && SECHS.find((s) => s.wahl === wahl)?.label}
      </h2>
      <p className="text-sm text-text-muted mb-4">
        {keinerDran && 'Gleiche Nummer, gleich jetzt. Erst wenn auch dann keiner rangeht, wird „nicht erreicht“ gespeichert und die Person ist bis morgen früh durch.'}
        {!keinerDran && schritt === 'abgenommen' && (nochEineAnwahl
          ? `Besetzt speichert sofort. Bei Klingelt durch und Mailbox wählst du die Person ${p.anwahlenProTag}-mal an.`
          : 'Besetzt, Klingelt durch und Mailbox speichern sofort.')}
        {schritt === 'ergebnis' && 'Ein Klick, danach nur noch der eine Folgeschritt.'}
        {schritt === 'folge' && SECHS.find((s) => s.wahl === wahl)?.hinweis}
      </p>

      {p.speicherFehler && (
        <p className="text-sm text-danger mb-3">Der Anruf konnte nicht gespeichert werden. Bitte noch einmal versuchen.</p>
      )}

      {keinerDran && (
        <div className="space-y-2">
          <button onClick={p.onNochmal} disabled={p.saving}
                  className="w-full py-3 max-md:py-4 max-md:text-base rounded-xl bg-accent text-sm font-semibold hover:bg-accent/90 disabled:opacity-50 flex items-center justify-center gap-2">
            <PhoneForwarded size={15} /> Nochmal wählen <span className="opacity-70 text-xs">W / ↵</span>
          </button>
          <button onClick={() => p.onSpeichern(keinerDran, {})} disabled={p.saving} className={knopf}
                  title="Speichert „nicht erreicht“ jetzt, ohne weitere Anwahl. Die Person ist trotzdem bis morgen früh durch.">
            Ohne weitere Anwahl speichern <span className="opacity-70 text-xs">S</span>
          </button>
        </div>
      )}

      {!keinerDran && schritt === 'abgenommen' && (
        <div className="space-y-2">
          <button onClick={besetzt} disabled={p.saving} className={knopf}
                  title="Wählt genau diese Zeit statt der 2. Anwahl und „ab morgen früh“">
            <PhoneOff size={15} /> Besetzt · in {p.besetztStunden} Std. nochmal <span className="opacity-70 text-xs">B</span>
          </button>
          <button onClick={() => keinerRan('NICHT ERREICHT')} disabled={p.saving} className={knopf}>
            <PhoneOff size={15} /> Klingelt durch, niemand dran <span className="opacity-70 text-xs">N</span>
          </button>
          <button onClick={() => keinerRan('MAILBOX')} disabled={p.saving} className={knopf}>
            <Voicemail size={15} /> Mailbox <span className="opacity-70 text-xs">M</span>
          </button>
          <button onClick={bot} disabled={p.saving} className={knopf}
                  title="Ein KI-Assistent hat abgenommen. Die nächste Person der Firma ist gleich dran.">
            <Bot size={15} /> Bot hat abgenommen <span className="opacity-70 text-xs">K</span>
          </button>
          <button
            onClick={() => setSchritt('ergebnis')}
            className={`w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
              sehrKurz ? 'bg-amber-500/90 text-black hover:bg-amber-500' : 'bg-accent hover:bg-accent/90'}`}
          >
            <Phone size={15} /> Ja, abgenommen <span className="opacity-70 text-xs">J</span>
          </button>
          {sehrKurz && (
            <p className="text-xs text-amber-500">
              Erst {p.elapsed} s seit dem Wählen (Mindest-Klingelzeit {p.mindestKlingelzeit} s). Wirklich abgenommen?
            </p>
          )}
          {p.nummerNichtVergeben}
          <button
            onClick={p.onVerwerfen}
            title="Der Anruf wird nicht gewertet und der Wählversuch zurückgenommen."
            className="w-full py-2.5 rounded-xl border border-danger/50 text-danger text-sm font-semibold hover:bg-danger/10"
          >
            Verwerfen (nicht tracken) <span className="opacity-70 text-xs">↵</span>
          </button>
        </div>
      )}

      {schritt === 'ergebnis' && (
        <div className="grid grid-cols-2 gap-2">
          {SECHS.map((s) => (
            <button key={s.wahl} onClick={() => { setWahl(s.wahl); setSchritt('folge'); }}
                    className="py-3 rounded-xl border border-stark bg-bg-elevated text-sm font-semibold hover:bg-bg-surface">
              {s.label} <span className="opacity-60 text-xs">{s.taste}</span>
            </button>
          ))}
        </div>
      )}

      {schritt === 'folge' && wahl && (
        <div className="space-y-3">
          {(wahl === 'TERMIN' || wahl === 'FOLLOW UP') && (
            <div className={terminOhneZeit ? 'rounded-lg ring-2 ring-amber-500/70 -mx-1 px-1 pt-1' : undefined}>
              <CallbackPicker
                presets={p.picker.presets} presetIdx={p.picker.presetIdx} callbackAt={p.picker.callbackAt}
                exactOpen={p.picker.exactOpen} hotkeys={false} kannSpeichern
                {...(wahl === 'TERMIN' ? { titel: 'Termin am', leerText: 'noch keine Zeit gewählt' } : {})}
                onPreset={p.picker.onPreset} onExact={p.picker.onExact}
                onToggleExact={p.picker.onToggleExact} onSavePreset={p.picker.onSavePreset}
              />
              {terminOhneZeit && (
                <p className="text-xs font-medium text-amber-500 -mt-3 mb-3">
                  Ein Termin braucht Datum und Uhrzeit.
                </p>
              )}
            </div>
          )}

          {wahl === 'LOST' && (
            <div className="rounded-xl border border-border-primary bg-bg-elevated/40 p-3">
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Wieder anrufen in</div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(new Set([p.keinInteresseTage, 30, 90])).map((n) => (
                  <button key={n} onClick={() => setKeinTage(n)}
                          className={`px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
                            keinTage === n ? 'bg-accent/15 border-accent text-accent'
                              : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'}`}>
                    {tageLabel(n)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {wahl === 'FALSCHER ANSPRECHPARTNER' && (
            <div className="space-y-2">
              <input
                value={wer} onChange={(e) => setWer(e.target.value)} disabled={weissNicht}
                placeholder="Name der Person, an die verwiesen wurde"
                className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <input
                value={position} onChange={(e) => setPosition(e.target.value)} disabled={weissNicht}
                placeholder="Position (freiwillig)"
                className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <label className="flex items-center gap-2 text-sm text-text-muted">
                <input type="checkbox" checked={weissNicht} onChange={(e) => setWeissNicht(e.target.checked)} />
                weiß nicht, wer zuständig ist
              </label>
              <p className="text-xs text-text-muted">
                Ist der Name in der Firma schon bekannt, wird diese Person genommen, sonst wird sie neu angelegt (auch ohne Nummer und Mail).
              </p>
            </div>
          )}

          <button onClick={speichern} disabled={!darfSpeichern}
                  className="w-full py-3 rounded-xl bg-accent text-sm font-semibold hover:bg-accent/90 disabled:opacity-50">
            Speichern <span className="opacity-70 text-xs">↵</span>
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <button onClick={zurueck}
                className="px-3 py-2 rounded-xl border border-border-primary text-sm text-text-muted hover:text-text-main hover:bg-bg-elevated">
          {schritt === 'abgenommen' && !keinerDran ? 'Zurück zum Call' : 'Zurück'} <span className="opacity-60 text-xs">Esc</span>
        </button>
        <button onClick={() => setKommentarOffen((v) => !v)} className="text-sm text-text-muted hover:text-text-main">
          {kommentarOffen ? '▾' : '▸'} Kommentar{p.notes.trim() ? ' (geschrieben)' : ''}
        </button>
      </div>
      {kommentarOffen && (
        <textarea
          value={p.notes} onChange={(e) => p.onNotes(e.target.value)} rows={3} autoFocus
          placeholder="Kommentar zum Gespräch ..."
          className="mt-2 w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent resize-none"
        />
      )}
    </div>
  );
}
